import React from "react";

/**
 * Generic error boundary so a single component crash never blanks the page.
 * Used by Ghost Recon (per build spec Fix 4) and reusable elsewhere.
 *
 * Usage:
 *   <ErrorBoundary fallbackMessage="Something went wrong loading Ghost Recon. Please refresh.">
 *     <GhostReconV2 />
 *   </ErrorBoundary>
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const msg = this.props.fallbackMessage || "Something went wrong. Please refresh.";
      return (
        <div
          data-testid="error-boundary-fallback"
          className="min-h-[40vh] flex flex-col items-center justify-center text-center px-6"
        >
          <div className="text-base font-semibold text-slate-100">{msg}</div>
          {this.state.error?.message && (
            <div className="mt-2 text-xs text-rose-300 font-mono max-w-xl break-words">
              {this.state.error.message}
            </div>
          )}
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-4 px-4 py-2 text-sm font-medium rounded-md bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-500/40 text-indigo-100 transition-colors"
            data-testid="error-boundary-reload-btn"
          >
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
